import { get, off, onChildAdded, onChildChanged, onChildRemoved, onValue, ref, remove, update } from 'firebase/database';
import { ChevronLeft, Copy, DoorOpen, Edit, Trash2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../Database';
import { useLoaderContext } from '../contexts/loader';
import { usePopUpContext } from '../contexts/Popup';
import { useUserContext } from '../contexts/user';
import copy from 'copy-to-clipboard';

const ChallengePage = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [challengeData, setChallengeData] = useState({});
    const [dataRendered, setDataRendered] = useState(false);
    const [addNewQuestion, setAddNewQuestion] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(-1);

    const { setIsLoading } = useLoaderContext();
    const { setMessageContent } = usePopUpContext();
    const { user, userInfo, userIndex, setUserInfo, appLanguage } = useUserContext();

    const questionInputRef = useRef();
    const correctAnswerRef = useRef();
    const firstWrongAnswerRef = useRef();
    const secondWrongAnswerRef = useRef();
    const thirdWrongAnswerRef = useRef();

    useEffect(() => {
        if (userInfo.id) {
            setIsLoading(prev => prev + 1);
            get(ref(db, "challenges/" + id)).then((snapShot) => {
                const challenge = snapShot.val();
                if (!challenge) {
                    update(ref(db, "users/" + userInfo.id), {
                        challenge: "",
                    }).then(() => {
                        setMessageContent({
                            title: appLanguage === "ar" ? "لم يتم العثور على التحدي" : "Challenge Not Found",
                            message: appLanguage === "ar" ? "لم يتم العثور على التحدي في قاعدة البيانات الخاصة بنا." : "Challenge is not found in our database.",
                        })
                        setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                        navigate("/live-challenge/" + (userInfo.role === "teacher" ? "create" : "join"));
                    })
                } else {
                    setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                    if (userInfo.role === "teacher" && challenge.creator_id === userInfo.id) {
                        setChallengeData(challenge);
                        setDataRendered(true);
                        if (challenge.status === "solving") {
                            navigate("/solving-challenge/" + id);
                        }
                    } else {
                        const user = challenge.users?.find(user => user.user_id === userInfo.id);
                        if (!user) {
                            setMessageContent({
                                title: appLanguage === "ar" ? "لم يتم العثور على التحدي" : "Challenge Not Found",
                                message: appLanguage === "ar" ? "لم يتم العثور على التحدي في قاعدة البيانات الخاصة بنا." : "Challenge is not found in our database.",
                            })
                            setIsLoading(prev => prev + 1);
                            update(ref(db, "users/" + userInfo.id), {
                                challenge: "",
                            }).then(() => {
                                navigate("/live-challenge/join");
                                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                            })
                        } else {
                            if (user.finished) {
                                navigate("/challenge-result/" + id);
                            } else {
                                setChallengeData(challenge);
                                setDataRendered(true);
                                if (challenge.status === "solving") {
                                    navigate("/solving-challenge/" + id);
                                }
                            }
                        }
                    }
                }
            }).catch(() => {
                setMessageContent({
                    title: appLanguage === "ar" ? "لم يتم العثور على التحدي" : "Challenge Not Found",
                    message: appLanguage === "ar" ? "لم يتم العثور على التحدي في قاعدة البيانات الخاصة بنا." : "Challenge is not found in our database.",
                })
                navigate("/live-challenge/" + (userInfo.role === "teacher" ? "create" : "join"));
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            })
        }
    }, [userInfo])

    useEffect(() => {
        if (editingQuestion !== -1) {
            setAddNewQuestion(true);
            const question = challengeData.questions[editingQuestion];
            setTimeout(() => {
                questionInputRef.current.value = question.text;
                correctAnswerRef.current.value = question.correct_answer;
                firstWrongAnswerRef.current.value = question.answers[1];
                secondWrongAnswerRef.current.value = question.answers[2];
                thirdWrongAnswerRef.current.value = question.answers[3];
            }, 0)
        }
    }, [editingQuestion])

    useEffect(() => {
        const userRemoved = () => {
            get(ref(db, "challenges/" + id)).then((snapShot) => {
                const challenge = snapShot.val();
                if (challenge) {
                    if (!challenge.users?.find(user => user.user_id === userInfo.id) && challenge.creator_id !== userInfo.id) {
                        setIsLoading(prev => prev + 1);
                        update(ref(db, "users/" + userInfo.id), {
                            challenge: "",
                        }).then(() => {
                            navigate("/live-challenge/" + (userInfo.role === "teacher" ? "create" : "join"));
                            setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                        })
                    } else {
                        setChallengeData(prev => ({
                            ...prev,
                            users: challenge.users ? [...challenge.users] : [],
                        }));
                    }
                } else {
                    setIsLoading(prev => prev + 1);
                    update(ref(db, "users/" + userInfo.id), {
                        challenge: "",
                    }).then(() => {
                        navigate("/live-challenge/" + (userInfo.role === "teacher" ? "create" : "join"));
                        setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                    })
                }
            })
        };
        const challengeRemoved = () => {
            get(ref(db, "challenges/" + id)).then((snapShot) => {
                if (!snapShot.val()) {
                    setIsLoading(prev => prev + 1);
                    update(ref(db, "users/" + userInfo.id), {
                        challenge: "",
                    }).then(() => {
                        setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                        setMessageContent({
                            title: appLanguage === "ar" ? "تمت إزالة التحدي" : "Challenge Removed",
                            message: appLanguage === "ar" ? "قام المعلم بإزالة التحدي." : "The teacher has removed the challenge.",
                        })
                        navigate("/live-challenge/join");
                        setUserInfo(prev => ({
                            ...prev,
                            challenge: "",
                        }))
                    })
                }
            })
        }
        const userAdded = () => {
            get(ref(db, "challenges/" + id + "/users")).then(snapShot => {
                const users = snapShot.val();
                setChallengeData(prev => ({
                    ...prev,
                    users,
                }))
            })
        }

        const statusUpdated = (snapShot) => {
            if (snapShot.val() === "solving") {
                navigate("/solving-challenge/" + id);
            }
        }

        if (challengeData.challenge_code) {
            onChildRemoved(ref(db, "challenges/" + id + "/users"), userRemoved);
            onChildAdded(ref(db, "challenges/" + id + "/users"), userAdded);
            onChildRemoved(ref(db, "challenges/" + id), challengeRemoved);
            onValue(ref(db, "challenges/" + id + "/status"), statusUpdated);
        }
    }, [dataRendered])

    const deleteUser = (i) => {
        setIsLoading(prev => prev + 1);
        get(ref(db, "/challenges/" + id + "/users")).then(snapShot => {
            const users = snapShot.val();
            const filteredUsers = users?.filter(user => user.user_id !== users[i].user_id);
            update(ref(db, "challenges/" + id), {
                users: filteredUsers
            }).then(() => {
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            }).catch(() => {
                setMessageContent({
                    title: appLanguage === "ar" ? "خطأ في الإزالة" : "Removing Error",
                    message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء إزالة الطالب من التحدي." : "Oops! something went wrong while removing the student from the challenge.",
                })
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            })
        }).catch(() => {
            setMessageContent({
                title: appLanguage === "ar" ? "خطأ في الإزالة" : "Removing Error",
                message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء إزالة الطالب من التحدي." : "Oops! something went wrong while removing the student from the challenge.",
            })
            setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
        })
    }

    const leaveChallenge = () => {
        const users = [...challengeData.users];
        const newUsers = users.filter(user => user.user_id !== userInfo.id);
        setIsLoading(prev => prev + 1);
        update(ref(db, "users/" + userInfo.id), {
            challenge: "",
        }).then(() => {
            update(ref(db, "challenges/" + id), {
                users: newUsers,
            }).then(() => {
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                navigate("/live-challenge/join");
            }).catch(() => {
                update(ref(db, "users/" + userInfo.id), {
                    challenge: id,
                })
                setMessageContent({
                    title: appLanguage === "ar" ? "خطأ في المغادرة" : "Leaving Error",
                    message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء إزالتك من التحدي." : "Oops! something went wrong while removing you from the challenge.",
                })
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            })
        }).catch(() => {
            setMessageContent({
                title: appLanguage === "ar" ? "خطأ في المغادرة" : "Leaving Error",
                message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء إزالتك من التحدي." : "Oops! something went wrong while removing you from the challenge.",
            })
            setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
        })
    }

    const emptyInputs = () => {
        questionInputRef.current.value = "";
        correctAnswerRef.current.value = "";
        firstWrongAnswerRef.current.value = "";
        secondWrongAnswerRef.current.value = "";
        thirdWrongAnswerRef.current.value = "";
    }

    const addQuestion = () => {
        const questionData = {
            text: questionInputRef.current.value.trim(),
            answers: [correctAnswerRef.current.value.trim(), firstWrongAnswerRef.current.value.trim(), secondWrongAnswerRef.current.value.trim(), thirdWrongAnswerRef.current.value.trim()],
            correct_answer: correctAnswerRef.current.value.trim(),
            question_id: new Date().getTime(),
        };
        if (questionData.text !== "" && questionData.answers.every(value => value !== "") && questionData.correct_answer !== "") {
            setIsLoading(prev => prev + 1);
            update(ref(db, "challenges/" + id), {
                questions: challengeData.questions ? [...challengeData.questions, questionData] : [questionData],
            }).then(() => {
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                setChallengeData(prev => ({
                    ...prev,
                    questions: prev.questions ? [...prev.questions, questionData] : [questionData],
                }))
                emptyInputs();
            }).catch(() => {
                setMessageContent({
                    title: appLanguage === "ar" ? "خطأ في الإضافة" : "Adding Error",
                    message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء إضافة السؤال." : "Oops! something went wrong while adding the question.",
                })
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            })
        } else {
            setMessageContent({
                title: appLanguage === "ar" ? "حقل (حقول) مفقود" : "Missing Field(s)",
                message: appLanguage === "ar" ? "يوجد حقل أو بعض الحقول فارغة." : "There is a field or some fields are empty.",
            })
        }
    }

    const deleteQuestion = (question_id) => {
        const questions = [...challengeData.questions] || [];
        const newQuestions = questions.filter(question => question.question_id !== question_id);
        setIsLoading(prev => prev + 1);
        update(ref(db, "challenges/" + id), {
            questions: newQuestions,
        }).then(() => {
            setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            setChallengeData(prev => ({
                ...prev,
                questions: newQuestions,
            }))
        }).catch(() => {
            setMessageContent({
                title: appLanguage === "ar" ? "خطأ في الحذف" : "Deleting Error",
                message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء حذف السؤال." : "Oops! something went wrong while deleting the question.",
            })
            setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
        })
    }

    const updateQuestion = () => {
        const question = challengeData.questions[editingQuestion];
        const newQuestionData = {
            text: questionInputRef.current.value.trim(),
            answers: [correctAnswerRef.current.value.trim(), firstWrongAnswerRef.current.value.trim(), secondWrongAnswerRef.current.value.trim(), thirdWrongAnswerRef.current.value.trim()],
            correct_answer: correctAnswerRef.current.value.trim(),
            question_id: question.question_id,
        }
        if (newQuestionData.text !== "" && newQuestionData.answers.every(value => value !== "") && newQuestionData.correct_answer !== "") {
            const newQuestions = challengeData.questions.map((question, i) => {
                if (i === editingQuestion) {
                    return newQuestionData;
                }
                return question;
            })
            setIsLoading(prev => prev + 1);
            update(ref(db, "challenges/" + id), {
                questions: newQuestions,
            }).then(() => {
                setEditingQuestion(-1);
                setAddNewQuestion(false);
                emptyInputs();
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                setChallengeData(prev => ({
                    ...prev,
                    questions: newQuestions,
                }))
            }).catch(() => {
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                setMessageContent({
                    title: appLanguage === "ar" ? "خطأ في التحديث" : "Updating Error",
                    message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء تحديث أسئلتك." : "Oops! something went wrong while updating your questions.",
                })
            })
        } else {
            setMessageContent({
                title: appLanguage === "ar" ? "حقل (حقول) مفقود" : "Missing Field(s)",
                message: appLanguage === "ar" ? "يوجد حقل أو بعض الحقول فارغة." : "There is a field or some fields are empty.",
            })
        }
    }

    const deleteChallenge = () => {
        if (userInfo.id === challengeData.creator_id) {
            setIsLoading(prev => prev + 1);
            remove(ref(db, "challenges/" + id))
                .then(() => {
                    setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                    setMessageContent({
                        title: appLanguage === "ar" ? "تم حذف التحدي" : "Challenge Deleted",
                        message: appLanguage === "ar" ? "تم حذف التحدي بنجاح!" : "Challenge has been deleted successfully!",
                    })
                    navigate("/live-challenge/create");
                }).catch(() => {
                    setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                    setMessageContent({
                        title: appLanguage === "ar" ? "خطأ في الحذف" : "Deleting Error",
                        message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء حذف التحدي الخاص بك." : "Oops! something went wrong while deleting your challenge.",
                    })
                })
        } else {
            setMessageContent({
                title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied",
                message: appLanguage === "ar" ? "يمكن للمعلم فقط حذف التحدي" : "Only the teacher can delete the challenge",
            })
        }
    }

    const startChallenge = () => {
        if(challengeData.users && challengeData.users.length > 1) {
            if (!challengeData.questions || challengeData.questions?.length < 3) {
                setMessageContent({
                    title: appLanguage === "ar" ? "لا يمكن البدء" : "Can't Start",
                    message: appLanguage === "ar" ? "عذراً! لا يمكنك بدء تحدي بعدد أسئلة أقل من 3 أسئلة." : "Sorry! but you can't start a challenge with number of questions less than 3 questions.",
                })
            } else {
                setIsLoading(prev => prev + 1);
                update(ref(db, "challenges/" + id), {
                    status: "solving",
                    challengeEndsAt: new Date().getTime() + (challengeData.questions.length * 30 * 1000) + 15000,
                }).then(() => {
                    setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                    navigate("/solving-challenge/" + id);
                })
            }
        }else {
            setMessageContent({
                title: appLanguage === "ar" ? "لا يمكن البدء" : "Can't Start",
                message: appLanguage === "ar" ? "عذراً! لا يمكنك بدء تحدي بأقل من طالبين." : "Sorry! but you can't start a challenge with less than 2 students.",
            })
        }
    }

    return (
        <div className="challenge_container">
            <header>
                <p>{appLanguage === "ar" ? "تحدي مباشر" : "Live Challenge"}</p>
            </header>
            <div className="content">
                <div className="main_content">
                    <div className="metadata">
                        <div className="teacher_name">
                            <p>{appLanguage === "ar" ? "المعلم: " : "Teacher: "}{challengeData?.creator_name}</p>
                        </div>
                        <div className="challenge_code">
                            <p>{id}</p>
                            <button onClick={() => { copy(id); setTimeout(() => { copy(id) }, 1); setMessageContent({ title: appLanguage === "ar" ? "تم نسخ الرمز" : "Code Copied", message: appLanguage === "ar" ? "تم نسخ رمز التحدي! ✅" : "Challenge code has been copied! ✅", }) }}><Copy strokeWidth={3} size={16} /></button>
                        </div>
                    </div>
                    <div className="all_students">
                        <p className="title">{appLanguage === "ar" ? "جميع الطلاب" : "All Students"}</p>
                        <div className="students_content">
                            {
                                !challengeData.users?.length ?
                                    <p className="no_students">{appLanguage === "ar" ? "لم ينضم أي طلاب" : "No Students Joined"}</p>
                                    : challengeData.users.map((user, i) => {
                                        return <div className="student" key={"user_" + i}>
                                            <div className="left_side">
                                                <p className="user_name" style={{ color: user.user_id === userInfo.id ? "#090" : "#fff", }}>{user.username}</p>
                                            </div>
                                            <div className="right_side">
                                                {userInfo.id === challengeData.creator_id ? <button onClick={() => deleteUser(i)}><Trash2 size={16} strokeWidth={3} /></button> : (user.user_id === userInfo.id ? <button onClick={leaveChallenge}><DoorOpen size={16} strokeWidth={3} /></button> : "")}
                                            </div>
                                        </div>
                                    })
                            }
                        </div>
                    </div>
                    {
                        challengeData?.creator_id === user ?
                            <div className="all_questions">
                                <p className="title">{appLanguage === "ar" ? "جميع الأسئلة" : "All Questions"}</p>
                                <div className="questions_content">
                                    {
                                        challengeData?.questions?.length ?
                                            challengeData.questions.map((question, i) => {
                                                return (
                                                    <div className="question" key={"question_" + i}>
                                                        <p className="question_text">{question.text}</p>
                                                        <div className="right_side">
                                                            <button className="edit" onClick={() => setEditingQuestion(i)}><Edit size={16} strokeWidth={3} /></button>
                                                            <button className="delete" onClick={() => window.confirm((appLanguage === "ar" ? "هل أنت متأكد أنك تريد حذف هذا السؤال '" : "Are you sure that you want to delete this question '") + question.text + "' ?") === true ? deleteQuestion(question.question_id) : console.log("Cancel Deleting Question")}><Trash2 size={16} strokeWidth={3} /></button>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                            : <p className="no_questions">{appLanguage === "ar" ? "لم تتم إضافة أسئلة بعد." : "No Added Questions Yet."}</p>
                                    }
                                    <button onClick={() => setAddNewQuestion(true)}>{appLanguage === "ar" ? "إضافة سؤال جديد" : "Add New Question"}</button>
                                </div>
                            </div> : undefined
                    }
                </div>

                <div className="challenge_controls">
                    <button className="start" onClick={() => userInfo.id === challengeData.creator_id ? startChallenge() : setMessageContent({ title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied", message: appLanguage === "ar" ? "يمكن للمعلم فقط بدء التحدي." : "Only the teacher can start the challenge.", })}>{appLanguage === "ar" ? "بدء التحدي" : "Start Challenge"}</button>
                    <button className="remove" onClick={() => userInfo.id === challengeData.creator_id ? (window.confirm(appLanguage === "ar" ? "هل أنت متأكد أنك تريد حذف هذا التحدي؟" : "Are you sure that you want to delete this challenge?") === true ? deleteChallenge() : console.log("Canceled deleting challenge.")) : setMessageContent({ title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied", message: appLanguage === "ar" ? "يمكن للمعلم فقط حذف التحدي." : "Only the teacher can delete the challenge.", })}>{appLanguage === "ar" ? "إزالة التحدي" : "Remove Challenge"}</button>
                </div>
            </div>
            {
                addNewQuestion ?
                    <div className="enter_question_container">
                        <div className="content">
                            <div className="top_content">
                                <p>{editingQuestion !== -1 ? (appLanguage === "ar" ? "تحديث السؤال" : "Update Question") : (appLanguage === "ar" ? "إضافة سؤال جديد" : "Add New Question")}</p>
                                <X onClick={() => { setAddNewQuestion(false); setEditingQuestion(-1) }} />
                            </div>
                            <div className="question_inputs">
                                <div className="input_container">
                                    <label htmlFor="question">{appLanguage === "ar" ? "السؤال" : "Question"}</label>
                                    <input type="text" ref={questionInputRef} className="question_input" placeholder=" " id="question" />
                                </div>
                                <div className="input_container">
                                    <label htmlFor="correct_answer">{appLanguage === "ar" ? "الإجابة الصحيحة" : "Correct Answer"}</label>
                                    <input type="text" ref={correctAnswerRef} className="correct_answer" placeholder=" " id="correct_answer" />
                                </div>
                                <div className="input_container">
                                    <label htmlFor="first_wrong_answer">{appLanguage === "ar" ? "الإجابة الخاطئة الأولى" : "First Wrong Answer"}</label>
                                    <input type="text" ref={firstWrongAnswerRef} className="wrong_answer" placeholder=" " id="first_wrong_answer" />
                                </div>
                                <div className="input_container">
                                    <label htmlFor="second_wrong_answer">{appLanguage === "ar" ? "الإجابة الخاطئة الثانية" : "Second Wrong Answer"}</label>
                                    <input type="text" ref={secondWrongAnswerRef} className="wrong_answer" placeholder=" " id="second_wrong_answer" />
                                </div>
                                <div className="input_container">
                                    <label htmlFor="third_wrong_answer">{appLanguage === "ar" ? "الإجابة الخاطئة الثالثة" : "Third Wrong Answer"}</label>
                                    <input type="text" ref={thirdWrongAnswerRef} className="wrong_answer" placeholder=" " id="third_wrong_answer" />
                                </div>
                            </div>
                            <button className={editingQuestion !== -1 ? "edit_question" : "add_question"} onClick={() => editingQuestion !== -1 ? updateQuestion() : addQuestion()}>{editingQuestion !== -1 ? (appLanguage === "ar" ? "تحديث السؤال" : "Update Question") : (appLanguage === "ar" ? "إضافة سؤال جديد" : "Add Question")}</button>
                        </div>
                    </div> : undefined
            }
        </div>
    )
}

export default ChallengePage;